import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { AddLotteryMutation as AddLottery, GetLotteriesForEventQuery } from '__generated__/graphql';
import { ChangeEvent } from 'react';
import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import AddLotteryMutation from './AddLotteryMutation';
import UI from '../../../common/UI';
import useLocale from '../../../common/useLocale';
import useMutation from '../../../api/useMutation';
import useTimeSlotFormatter from '../../../common/Tickets/useTimeSlotFormatter';
import useValidation from '../../../common/useValidation';

const addLotterySchema = yup.object().shape({
  title: yup.string().ensure().required(),
  ticket_id: yup.string().ensure().required(),
  promotion_id: yup.string().ensure().required(),
});

type Event = GetLotteriesForEventQuery['project']['event'];

export interface AddLotteryFormProps {
  event: Event;
  onSuccess?: (lottery: AddLottery['addLottery']) => void;
}

const AddLotteryForm = ({ event, onSuccess }: AddLotteryFormProps) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();
  const formatTimeSlot = useTimeSlotFormatter();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      title: '',
      ticket_id: null,
      promotion_id: null,
      time_slot_id: null,
    },
  });

  const tickets = event.tickets.filter((ticket) => ticket.allow_lottery_confirmations);
  const selectedTicket = tickets.find((ticket) => ticket.id === form.values.ticket_id);

  const handleTicketChange = (changeEvent: ChangeEvent<HTMLSelectElement>) => {
    const ticket = tickets.find((ticket) => ticket.id === changeEvent.target.value);

    if (!ticket) {
      return;
    }

    form.set((values) => ({
      ...values,
      ticket_id: ticket.id,
      promotion_id: ticket.promotions.length === 1 ? ticket.promotions[0].id : null,
      time_slot_id: null,
    }));
  };

  const handlePromotionChange = (changeEvent: ChangeEvent<HTMLSelectElement>) => {
    const promotion = selectedTicket.promotions.find((promotion) => promotion.id === changeEvent.target.value);

    form.set((values) => ({
      ...values,
      promotion_id: promotion?.id,
    }));
  };

  const handleTimeSlotChange = (changeEvent: ChangeEvent<HTMLSelectElement>) => {
    const timeSlot = selectedTicket.time_slots.find((timeSlot) => timeSlot.id === changeEvent.target.value);

    form.set((values) => ({
      ...values,
      time_slot_id: timeSlot?.id,
    }));
  };

  const [addLottery, { loading, error }] = useMutation(
    AddLotteryMutation,
    {
      variables: {
        input: {
          event_id: event.id,
          info: form.values,
        },
      },
      onCompleted: ({ addLottery: lottery }) => {
        onSuccess?.(lottery);
        notifier.success(t('notifications.lottery_added'));
      },
      onError: () => {
        notifier.error(t('something_went_wrong'));
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: addLotterySchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addLottery()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('add_a_lottery')}
          </UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_lottery_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="AddLotteryTitle">
            {t('title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {t('not_visible_to_participants')}
          </UI.InputDescription>
          <UI.DebouncedInput
            {...form.text('title')}
            maxLength={255}
            autoFocus
            id="AddLotteryTitle"
          />
          <UI.ErrorMessages attribute={t('title')} errors={errors.title} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.ticket_id, touched: form.getTouched('ticket_id') }}>
          <UI.InputLabel htmlFor="AddLotteryTicket">
            {t('ticket')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {t('lottery_ticket_description')}
          </UI.InputDescription>
          <UI.Select
            onChange={handleTicketChange}
            value={form.values.ticket_id || ''}
            id="AddLotteryTicket"
          >
            {!form.values.ticket_id && <option />}
            {tickets.map((ticket, index) => (
              <option value={ticket.id} key={index}>
                {ticket.title}
              </option>
            ))}
          </UI.Select>
          <UI.ErrorMessages attribute={t('ticket')} errors={errors.ticket_id} />
        </UI.InputGroup>

        {selectedTicket && selectedTicket.promotions.length > 1 && (
          <UI.InputGroup sc={{ valid: !errors.promotion_id, touched: form.getTouched('promotion_id') }}>
            <UI.InputLabel htmlFor="AddLotteryPromotion">
              {t('promotion')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.Select
              onChange={handlePromotionChange}
              value={form.values.promotion_id || ''}
              id="AddLotteryPromotion"
            >
              {!form.values.promotion_id && <option />}
              {selectedTicket.promotions.map((promotion, index) => (
                <option value={promotion.id} key={index}>
                  {promotion.title || t('standard')}
                  {' • '}
                  {formatCurrency(promotion.amount)}
                </option>
              ))}
            </UI.Select>
            <UI.ErrorMessages attribute={t('promotion')} errors={errors.promotion_id} />
          </UI.InputGroup>
        )}

        {selectedTicket && selectedTicket.time_slots.length > 1 && (
          <UI.InputGroup sc={{ valid: !errors.time_slot_id, touched: form.getTouched('time_slot_id') }}>
            <UI.InputLabel htmlFor="AddLotteryTimeSlot">
              {t('time_slot')}
            </UI.InputLabel>
            <UI.Select
              onChange={handleTimeSlotChange}
              value={form.values.time_slot_id || ''}
              id="AddLotteryTimeSlot"
            >
              <option value="">
                {t('no_time_slot')}
              </option>
              {selectedTicket.time_slots.map((timeSlot, index) => (
                <option value={timeSlot.id} key={index}>
                  {formatTimeSlot(timeSlot)}
                  {timeSlot.title && ` • ${timeSlot.title}`}
                </option>
              ))}
            </UI.Select>
            <UI.ErrorMessages attribute={t('time_slot')} errors={errors.time_slot_id} />
          </UI.InputGroup>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('add_lottery')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default AddLotteryForm;
